import { createSelector } from 'reselect';
import { SavedAPIAddress } from '~/types/APIAddress';
import { RateAtCheckoutErrorArray } from '~/types/RatesAtCheckout';
import {
  extractRequestable,
  requestableHasFailed,
  requestableHasSucceeded,
  requestableIsLoading,
} from '~/utils/fetchable';
import { ApplicationState } from '../store';

export const getRatesAtCheckoutStore = (state: ApplicationState) => state.ratesAtCheckout;

export const showAddShippingOptionModal = createSelector(
  getRatesAtCheckoutStore,
  (ratesAtCheckoutStore) => ratesAtCheckoutStore.addShippingOptionModalIsOpen,
);

export const getShippingOptions = createSelector(
  getRatesAtCheckoutStore,
  (ratesAtCheckoutStore) => {
    return extractRequestable(ratesAtCheckoutStore.shippingOptions) || [];
  },
);

export const getLiveRatePackageTemplate = createSelector(
  getRatesAtCheckoutStore,
  (ratesAtCheckoutStore) => extractRequestable(ratesAtCheckoutStore.liveRatePackage),
);

export const getLiveRateErrors = createSelector(getRatesAtCheckoutStore, (ratesAtCheckoutStore) => {
  const requestTypes = [
    ratesAtCheckoutStore.createShippingOptionStatus,
    ratesAtCheckoutStore.updateShippingOptionStatus,
    ratesAtCheckoutStore.deleteShippngOptionStatus,
  ];

  const errors: RateAtCheckoutErrorArray = [];
  for (const type of requestTypes) {
    if (type.kind === 'failed') {
      errors.push(...type.error);
    }
  }
  return errors;
});

// returns true if the packageTemplate has been made and has not yet failed
export const getIsPackageTemplateLoading = createSelector(
  getRatesAtCheckoutStore,
  (providersStore): boolean => {
    return requestableIsLoading(providersStore.liveRatePackage);
  },
);

// returns true if the shippingOptions has been made and has not yet failed
// (e.g. to determine whether to make a new request)
export const getIsShippingOptionsRequestInitiated = createSelector(
  getRatesAtCheckoutStore,
  (providersStore): boolean => {
    const request = providersStore.shippingOptions;
    return requestableIsLoading(request) || requestableHasSucceeded(request);
  },
);

// returns true if the shippingOptions has finished, regardless of whether it
// succeeded or failed (e.g. for determining whether to show a loading spinner)
export const getIsShippingOptionsRequestComplete = createSelector(
  getRatesAtCheckoutStore,
  (providersStore): boolean => {
    const request = providersStore.shippingOptions;
    return requestableHasFailed(request) || requestableHasSucceeded(request);
  },
);

export const getLiveRateSenderAddress = createSelector(
  getRatesAtCheckoutStore,
  (providersStore): Maybe<SavedAPIAddress> => {
    return extractRequestable(providersStore.liveRateSenderAddress);
  },
);

export const getIsSenderAddressLoaded = createSelector(
  getRatesAtCheckoutStore,
  (providersStore): boolean => {
    const request = providersStore.liveRateSenderAddress;
    return requestableHasFailed(request) || requestableHasSucceeded(request);
  },
);
